import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, Settings as SettingsIcon, Mail, BarChart3, FileText, Inbox, Shield, Trash2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function SettingsManager() {
  const [settings, setSettings] = useState({
    notification_email: '',
    company_email: '',
    company_phone: '',
    company_phone_2: '',
    address_1_name: '',
    address_1_street: '',
    address_1_city: '',
    address_2_name: '',
    address_2_street: '',
    address_2_city: '',
    address_1_maps_url: '',
    address_2_maps_url: '',
    footer_description_en: '',
    footer_description_bg: '',
    footer_description_de: '',
    footer_facebook_url: '',
    footer_linkedin_url: '',
    footer_instagram_url: '',
    footer_copyright_en: '',
    footer_copyright_bg: '',
    footer_copyright_de: '',
    send_email_notifications: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Tetralog Ltd.',
    google_analytics_id: '',
    meta_pixel_id: ''
  });
  const [submissions, setSubmissions] = useState({ contacts: [], quotes: [] });
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [emailStatus, setEmailStatus] = useState({ smtp_configured: false });
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, submissionsRes, emailStatusRes, loginRes] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/submissions`),
        axios.get(`${API}/admin/email-status`),
        axios.get(`${API}/admin/login-attempts?limit=20`)
      ]);
      setSettings(settingsRes.data);
      const submissionsData = submissionsRes.data;
      setSubmissions({
        contacts: submissionsData.contact_messages || submissionsData.contacts || [],
        quotes: submissionsData.quote_requests || submissionsData.quotes || []
      });
      setEmailStatus(emailStatusRes.data);
      setLoginAttempts(loginRes.data.attempts || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const clearLoginAttempts = async () => {
    if (!window.confirm('Clear all login attempt logs?')) return;
    try {
      await axios.delete(`${API}/admin/login-attempts`);
      setLoginAttempts([]);
      toast.success('Login attempts cleared');
    } catch (error) {
      toast.error('Failed to clear login attempts');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Settings saved successfully!');
      const emailStatusRes = await axios.get(`${API}/admin/email-status`);
      setEmailStatus(emailStatusRes.data);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setIsSendingTest(true);
    try {
      const response = await axios.post(`${API}/admin/test-email`, { to_email: testEmail });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Delete this contact message?')) return;
    try {
      await axios.delete(`${API}/admin/submissions/contact/${id}`);
      toast.success('Contact message deleted!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting contact message');
    }
  };

  const handleDeleteQuote = async (id) => {
    if (!window.confirm('Delete this quote request?')) return;
    try {
      await axios.delete(`${API}/admin/submissions/quote/${id}`);
      toast.success('Quote request deleted!');
      fetchData();
    } catch (error) {
      toast.error('Error deleting quote request');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-[#C41E3A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={() => setActiveTab('settings')} variant={activeTab === 'settings' ? 'default' : 'outline'} className={`rounded-full ${activeTab === 'settings' ? 'bg-[#C41E3A] text-white' : ''}`}>
          <SettingsIcon className="w-4 h-4 mr-2" /> Contact Info
        </Button>
        <Button onClick={() => setActiveTab('email')} variant={activeTab === 'email' ? 'default' : 'outline'} className={`rounded-full ${activeTab === 'email' ? 'bg-[#C41E3A] text-white' : ''}`}>
          <Mail className="w-4 h-4 mr-2" /> Email Config {emailStatus.smtp_configured ? '✓' : ''}
        </Button>
        <Button onClick={() => setActiveTab('analytics')} variant={activeTab === 'analytics' ? 'default' : 'outline'} className={`rounded-full ${activeTab === 'analytics' ? 'bg-[#C41E3A] text-white' : ''}`}>
          <BarChart3 className="w-4 h-4 mr-2" /> Analytics {(settings.google_analytics_id || settings.meta_pixel_id) ? '✓' : ''}
        </Button>
        <Button onClick={() => setActiveTab('footer')} variant={activeTab === 'footer' ? 'default' : 'outline'} className={`rounded-full ${activeTab === 'footer' ? 'bg-[#C41E3A] text-white' : ''}`}>
          <FileText className="w-4 h-4 mr-2" /> Footer
        </Button>
        <Button onClick={() => setActiveTab('contacts')} variant={activeTab === 'contacts' ? 'default' : 'outline'} className={`rounded-full ${activeTab === 'contacts' ? 'bg-[#C41E3A] text-white' : ''}`}>
          <Inbox className="w-4 h-4 mr-2" /> Messages ({submissions.contacts?.length || 0})
        </Button>
        <Button onClick={() => setActiveTab('quotes')} variant={activeTab === 'quotes' ? 'default' : 'outline'} className={`rounded-full ${activeTab === 'quotes' ? 'bg-[#C41E3A] text-white' : ''}`}>
          <Inbox className="w-4 h-4 mr-2" /> Quotes ({submissions.quotes?.length || 0})
        </Button>
        <Button onClick={() => setActiveTab('security')} variant={activeTab === 'security' ? 'default' : 'outline'} className={`rounded-full ${activeTab === 'security' ? 'bg-[#C41E3A] text-white' : ''}`}>
          <Shield className="w-4 h-4 mr-2" /> Security
        </Button>
      </div>

      {/* Security Tab */}
      {activeTab === 'security' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Login Attempts</h3>
                <p className="text-sm text-slate-500">Last 20 login attempts to admin panel</p>
              </div>
              {loginAttempts.length > 0 && (
                <Button variant="outline" size="sm" onClick={clearLoginAttempts} className="text-red-500 rounded-full">
                  <Trash2 className="w-4 h-4 mr-1" /> Clear Logs
                </Button>
              )}
            </div>
            
            {loginAttempts.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No login attempts recorded yet</p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {loginAttempts.map((attempt, index) => (
                  <div key={attempt.id || index} className={`p-3 rounded-xl border ${attempt.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${attempt.success ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className={`font-medium ${attempt.success ? 'text-green-700' : 'text-red-700'}`}>
                          {attempt.success ? '✓ Successful' : '✗ Failed'}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">{new Date(attempt.timestamp).toLocaleString('bg-BG')}</span>
                    </div>
                    <div className="mt-1 text-sm text-slate-600 flex flex-wrap items-center gap-2">
                      <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">{attempt.ip_address}</span>
                      {attempt.browser && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">🌐 {attempt.browser}</span>}
                      {attempt.os && <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">💻 {attempt.os}</span>}
                      {attempt.device && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs">📱 {attempt.device}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>🛡️ Rate Limiting Active:</strong> Max 5 login attempts per minute per IP address.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Configuration Tab */}
      {activeTab === 'email' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Email Configuration</h3>
            <p className="text-slate-500 text-sm mb-6">Configure SMTP settings to receive email notifications.</p>
            
            <div className={`p-4 rounded-xl mb-6 ${emailStatus.smtp_configured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${emailStatus.smtp_configured ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <span className={`font-medium ${emailStatus.smtp_configured ? 'text-green-700' : 'text-yellow-700'}`}>
                  {emailStatus.smtp_configured ? 'Email notifications are active' : 'Email not configured'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">SMTP Server</h4>
                <div>
                  <Label>SMTP Host *</Label>
                  <Input value={settings.smtp_host || ''} onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })} placeholder="e.g. smtp.gmail.com" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input type="number" value={settings.smtp_port || 587} onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })} placeholder="587" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>SMTP Username *</Label>
                  <Input value={settings.smtp_user || ''} onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })} placeholder="your-email@domain.com" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>SMTP Password *</Label>
                  <Input type="password" value={settings.smtp_password || ''} onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })} placeholder="••••••••" className="mt-1 rounded-xl" />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">Sender Details</h4>
                <div>
                  <Label>From Email *</Label>
                  <Input type="email" value={settings.smtp_from_email || ''} onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })} placeholder="noreply@tetralog.bg" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>From Name</Label>
                  <Input value={settings.smtp_from_name || ''} onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })} placeholder="Tetralog Ltd." className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Notification Email *</Label>
                  <Input type="email" value={settings.notification_email || ''} onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })} placeholder="info@tetralog.bg" className="mt-1 rounded-xl" />
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-slate-800 mb-3">Send Test Email</h4>
                  <div className="flex gap-2">
                    <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" className="rounded-xl flex-1" />
                    <Button onClick={handleSendTestEmail} disabled={isSendingTest || !emailStatus.smtp_configured} className="bg-slate-800 text-white hover:bg-slate-700 rounded-full">
                      {isSendingTest ? 'Sending...' : 'Send Test'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full mt-6">
              <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Email Settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Analytics</h3>
            <p className="text-slate-500 text-sm mb-6">Configure Google Analytics and Meta Pixel.</p>
            
            <div className="space-y-4 max-w-md">
              <div>
                <Label>Google Analytics Measurement ID</Label>
                <Input placeholder="G-XXXXXXXXXX" value={settings.google_analytics_id || ''} onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Meta Pixel ID</Label>
                <Input placeholder="XXXXXXXXXXXXXXXX" value={settings.meta_pixel_id || ''} onChange={(e) => setSettings({ ...settings, meta_pixel_id: e.target.value })} className="rounded-xl mt-1" />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
              <p className="text-sm text-amber-800">Analytics will only load when visitors accept cookies. GDPR compliant.</p>
            </div>

            <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full mt-6">
              <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Analytics Settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information Settings</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">Email & Phone</h4>
                <div>
                  <Label>Company Email</Label>
                  <Input type="email" value={settings.company_email} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} placeholder="contact@tetralog.bg" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Company Phone 1</Label>
                  <Input type="tel" value={settings.company_phone} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} placeholder="+359 888 123 456" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Company Phone 2 (optional)</Label>
                  <Input type="tel" value={settings.company_phone_2} onChange={(e) => setSettings({ ...settings, company_phone_2: e.target.value })} placeholder="+359 888 654 321" className="mt-1 rounded-xl" />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-slate-800 border-b pb-2">Addresses</h4>
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <p className="text-sm font-medium text-slate-600">Address 1 (Primary)</p>
                  <Input value={settings.address_1_name} onChange={(e) => setSettings({ ...settings, address_1_name: e.target.value })} placeholder="Company Name" className="rounded-xl" />
                  <Input value={settings.address_1_street} onChange={(e) => setSettings({ ...settings, address_1_street: e.target.value })} placeholder="Street / Area" className="rounded-xl" />
                  <Input value={settings.address_1_city} onChange={(e) => setSettings({ ...settings, address_1_city: e.target.value })} placeholder="City, Country" className="rounded-xl" />
                  <Input value={settings.address_1_maps_url} onChange={(e) => setSettings({ ...settings, address_1_maps_url: e.target.value })} placeholder="Google Maps URL" className="rounded-xl" />
                </div>
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <p className="text-sm font-medium text-slate-600">Address 2 (Optional)</p>
                  <Input value={settings.address_2_name} onChange={(e) => setSettings({ ...settings, address_2_name: e.target.value })} placeholder="Company Name" className="rounded-xl" />
                  <Input value={settings.address_2_street} onChange={(e) => setSettings({ ...settings, address_2_street: e.target.value })} placeholder="Street / Area" className="rounded-xl" />
                  <Input value={settings.address_2_city} onChange={(e) => setSettings({ ...settings, address_2_city: e.target.value })} placeholder="City, Country" className="rounded-xl" />
                  <Input value={settings.address_2_maps_url} onChange={(e) => setSettings({ ...settings, address_2_maps_url: e.target.value })} placeholder="Google Maps URL" className="rounded-xl" />
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full mt-6">
              <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Footer Tab */}
      {activeTab === 'footer' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6">Footer Settings</h3>
            
            <div className="space-y-6">
              {/* Footer Description with Language Tabs */}
              <div>
                <h4 className="font-medium text-slate-800 border-b pb-2 mb-4">Footer Description</h4>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-4">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                    ))}
                  </TabsList>
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code}>
                      <div className={`p-4 rounded-xl ${lang.bg}`}>
                        <Textarea
                          value={settings[`footer_description_${lang.code}`] || ''}
                          onChange={(e) => setSettings({ ...settings, [`footer_description_${lang.code}`]: e.target.value })}
                          placeholder={`Footer description (${lang.name})`}
                          className="bg-white rounded-xl"
                          rows={3}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              {/* Social Media Links */}
              <div>
                <h4 className="font-medium text-slate-800 border-b pb-2 mb-4">Social Media Links</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Facebook URL</Label>
                    <Input value={settings.footer_facebook_url} onChange={(e) => setSettings({ ...settings, footer_facebook_url: e.target.value })} placeholder="https://facebook.com/tetralog" className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label>LinkedIn URL</Label>
                    <Input value={settings.footer_linkedin_url} onChange={(e) => setSettings({ ...settings, footer_linkedin_url: e.target.value })} placeholder="https://linkedin.com/company/tetralog" className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label>Instagram URL</Label>
                    <Input value={settings.footer_instagram_url} onChange={(e) => setSettings({ ...settings, footer_instagram_url: e.target.value })} placeholder="https://instagram.com/tetralog" className="mt-1 rounded-xl" />
                  </div>
                </div>
              </div>

              {/* Copyright Text with Language Tabs */}
              <div>
                <h4 className="font-medium text-slate-800 border-b pb-2 mb-4">Copyright Text</h4>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-4">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                    ))}
                  </TabsList>
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code}>
                      <div className={`p-4 rounded-xl ${lang.bg}`}>
                        <Input
                          value={settings[`footer_copyright_${lang.code}`] || ''}
                          onChange={(e) => setSettings({ ...settings, [`footer_copyright_${lang.code}`]: e.target.value })}
                          placeholder={`© 2026 Tetralog Ltd. (${lang.name})`}
                          className="bg-white rounded-xl"
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Footer Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Messages Tab */}
      {activeTab === 'contacts' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Contact Form Submissions</h3>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Processed</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full"></span> Pending</span>
              </div>
            </div>
            {submissions.contacts?.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No contact messages yet.</p>
            ) : (
              <div className="space-y-4">
                {submissions.contacts?.map((msg) => (
                  <div key={msg.id} className={`border rounded-xl p-4 transition-colors ${msg.is_processed ? 'border-green-200 bg-green-50/30' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={async () => {
                            try {
                              const endpoint = msg.is_processed ? 'unprocess' : 'process';
                              await axios.put(`${API}/admin/submissions/contact/${msg.id}/${endpoint}`);
                              fetchData();
                              toast.success(msg.is_processed ? 'Marked as pending' : 'Marked as processed');
                            } catch (error) {
                              toast.error('Failed to update status');
                            }
                          }}
                          className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            msg.is_processed 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-slate-300 hover:border-green-400'
                          }`}
                        >
                          {msg.is_processed && <Check className="w-3 h-3" />}
                        </button>
                        <div>
                          <h4 className="font-semibold text-slate-900">{msg.name}</h4>
                          <p className="text-sm text-slate-500">{msg.email} • {msg.phone || 'No phone'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{new Date(msg.created_at).toLocaleString('bg-BG')}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(msg.id)} className="text-red-500 hover:bg-red-50 p-1 h-auto">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-slate-700 mb-1 ml-8">Subject: {msg.subject}</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap ml-8">{msg.message}</p>
                    {msg.processed_at && (
                      <p className="text-xs text-green-600 mt-2 ml-8">Processed: {new Date(msg.processed_at).toLocaleString('bg-BG')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quote Requests Tab */}
      {activeTab === 'quotes' && (
        <Card className="bg-white rounded-3xl shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Quote Requests</h3>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Processed</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full"></span> Pending</span>
              </div>
            </div>
            {submissions.quotes?.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No quote requests yet.</p>
            ) : (
              <div className="space-y-4">
                {submissions.quotes?.map((quote) => (
                  <div key={quote.id} className={`border rounded-xl p-4 transition-colors ${quote.is_processed ? 'border-green-200 bg-green-50/30' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={async () => {
                            try {
                              const endpoint = quote.is_processed ? 'unprocess' : 'process';
                              await axios.put(`${API}/admin/submissions/quote/${quote.id}/${endpoint}`);
                              fetchData();
                              toast.success(quote.is_processed ? 'Marked as pending' : 'Marked as processed');
                            } catch (error) {
                              toast.error('Failed to update status');
                            }
                          }}
                          className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            quote.is_processed 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-slate-300 hover:border-green-400'
                          }`}
                        >
                          {quote.is_processed && <Check className="w-3 h-3" />}
                        </button>
                        <div>
                          <h4 className="font-semibold text-slate-900">{quote.name}</h4>
                          <p className="text-sm text-slate-500">{quote.email} • {quote.phone}</p>
                          {quote.company && <p className="text-sm text-slate-500">Company: {quote.company}</p>}
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${quote.is_processed ? 'bg-green-100 text-green-700' : 'bg-[#C41E3A]/10 text-[#C41E3A]'}`}>
                            #{quote.id?.slice(0, 8).toUpperCase()}
                          </span>
                          <p className="text-xs text-slate-400 mt-1">{new Date(quote.created_at).toLocaleString('bg-BG')}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteQuote(quote.id)} className="text-red-500 hover:bg-red-50 p-1 h-auto">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm ml-8">
                      <div>
                        <span className="text-slate-400">Service:</span>
                        <p className="text-slate-700 font-medium">{quote.service_type}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Origin:</span>
                        <p className="text-slate-700">{quote.origin}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Destination:</span>
                        <p className="text-slate-700">{quote.destination}</p>
                      </div>
                    </div>
                    <div className="mt-3 ml-8">
                      <span className="text-slate-400 text-sm">Cargo Description:</span>
                      <p className="text-sm text-slate-700">{quote.cargo_description}</p>
                    </div>
                    {quote.processed_at && (
                      <p className="text-xs text-green-600 mt-2 ml-8">Processed: {new Date(quote.processed_at).toLocaleString('bg-BG')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
